import type {
  DraftMessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { MongoTeamId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { CustomerEvent } from "@redotech/redo-customer-event-common/customer-event-definition";
import { CustomerEventSchema } from "@redotech/redo-customer-event-common/customer-event-zod-schema";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import type { SupportTicketDraftsApi } from "@redotech/redo-support-v2/drafts/support-ticket-drafts-collection";
import type { SupportTicketListApi } from "@redotech/redo-support-v2/query/support-ticket-list-collection";
import type {
  OrderWithPickStatus,
  SupportTicketRelatedRecordsApi,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
import type { SupportTicketTranscriptApi } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { profileSupportTicketRequest } from "../profiling/support-ticket-load-profiler";
import { catchApiErrors, catchApiErrorsSilent } from "./error-handling";

export interface CreateMerchantSupportTicketDataSourceOptions {
  effectApi: EffectApiContext;
  queryClient?: QueryClient;
}

export interface MerchantSupportTicketDataSource {
  list: SupportTicketListApi;
  transcript: SupportTicketTranscriptApi;
  relatedRecords: SupportTicketRelatedRecordsApi;
  drafts: SupportTicketDraftsApi;
  getSupportTicketSummary(
    supportTicketId: SupportTicketId,
  ): Promise<{ summary: string | null }>;
}

const SUPPORT_TICKET_STALE_TIME_MS = 5 * 60 * 1000;
const SUPPORT_TICKET_DETAIL_PAGE_LIMIT = 200;

export const supportTicketSummaryBaseQueryKey = [
  "support-ticket-summary",
] as const;

export function supportTicketSummaryQueryKey(supportTicketId: SupportTicketId) {
  return [...supportTicketSummaryBaseQueryKey, supportTicketId] as const;
}

async function fetchSupportTicketSummary(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<{ summary: string | null }> {
  const client = effectApi.getClient();
  return profileSupportTicketRequest(
    "getSupportTicketSummary",
    { supportTicketId },
    () =>
      effectApi.runPromise(
        catchApiErrorsSilent(
          client.supportTicket.getSupportTicketSummary({
            path: { id: supportTicketId },
          }),
        ),
      ),
  );
}

export function supportTicketSummaryQueryOptions(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
) {
  return queryOptions({
    queryKey: supportTicketSummaryQueryKey(supportTicketId),
    queryFn: () => fetchSupportTicketSummary(effectApi, supportTicketId),
    staleTime: SUPPORT_TICKET_STALE_TIME_MS,
    meta: { showErrorToast: true },
  });
}

export function createMerchantSupportTicketDataSource({
  effectApi,
  queryClient,
}: CreateMerchantSupportTicketDataSourceOptions): MerchantSupportTicketDataSource {
  const client = effectApi.getClient();
  const invalidateTicketQueries =
    <T>(supportTicketId: SupportTicketId) =>
    (result: T) => {
      void queryClient?.invalidateQueries({
        queryKey: supportTicketSummaryQueryKey(supportTicketId),
      });
      return result;
    };

  return {
    list: {
      getSupportTicket: (supportTicketId) =>
        profileSupportTicketRequest(
          "getSupportTicket",
          { supportTicketId },
          () =>
            effectApi.runPromise(
              catchApiErrors(
                client.supportTicket.getSupportTicket({
                  path: { id: supportTicketId },
                }),
              ),
            ),
        ),
      listSupportTickets: (urlParams) =>
        profileSupportTicketRequest("listSupportTickets", urlParams, () =>
          effectApi.runPromise(
            catchApiErrors(
              client.supportTicket.listSupportTickets({ urlParams }),
            ),
          ),
        ),
      updateSupportTicket: (supportTicketId, payload) =>
        profileSupportTicketRequest(
          "updateSupportTicket",
          { supportTicketId, fields: Object.keys(payload) },
          () =>
            effectApi
              .runPromise(
                catchApiErrors(
                  client.supportTicket.updateSupportTicket({
                    path: { id: supportTicketId },
                    payload,
                  }),
                ),
              )
              .then(invalidateTicketQueries(supportTicketId))
              .then(() => undefined),
        ),
    },
    transcript: {
      getThreads: (supportTicketId) =>
        profileSupportTicketRequest(
          "getSupportTicketThreads",
          { supportTicketId },
          () =>
            effectApi.runPromise(
              catchApiErrorsSilent(
                client.supportTicket.getSupportTicketThreads({
                  path: { id: supportTicketId },
                  urlParams: { limit: SUPPORT_TICKET_DETAIL_PAGE_LIMIT },
                }),
              ),
            ),
        ),
      getMessages: (supportTicketId, cursor) =>
        profileSupportTicketRequest(
          "getSupportTicketMessages",
          { supportTicketId, olderPage: cursor !== undefined },
          () =>
            effectApi.runPromise(
              catchApiErrorsSilent(
                client.supportTicket.getSupportTicketMessages({
                  path: { id: supportTicketId },
                  // Newest page, so a long ticket still shows its new replies.
                  urlParams: {
                    limit: SUPPORT_TICKET_DETAIL_PAGE_LIMIT,
                    direction: "backward",
                    ...(cursor ? { cursor } : {}),
                  },
                }),
              ),
            ),
        ),
      getTimelineEvents: (supportTicketId) =>
        profileSupportTicketRequest(
          "getSupportTicketTimelineEvents",
          { supportTicketId },
          () =>
            effectApi.runPromise(
              catchApiErrorsSilent(
                client.supportTicket.getSupportTicketTimelineEvents({
                  path: { id: supportTicketId },
                }),
              ),
            ),
        ),
      getThreadMessages: (supportTicketId, threadId) =>
        profileSupportTicketRequest(
          "getChannelThreadMessages",
          { supportTicketId, threadId },
          () =>
            effectApi.runPromise(
              catchApiErrorsSilent(
                client.supportTicket.getChannelThreadMessages({
                  path: { id: supportTicketId, threadId },
                  urlParams: { limit: SUPPORT_TICKET_DETAIL_PAGE_LIMIT },
                }),
              ),
            ),
        ),
    },
    relatedRecords: {
      getOrders: async (teamId, { emailFallback, mongoCustomerId, limit }) => {
        return profileSupportTicketRequest(
          "getSupportRelatedOrders",
          {
            hasEmail: !!emailFallback,
            hasCustomerId: !!mongoCustomerId,
            teamId,
          },
          async () => {
            const result = await effectApi.runPromise(
              catchApiErrors(
                client.supportTicketRelatedRecords.getSupportRelatedOrders({
                  urlParams: {
                    teamId: MongoTeamId.fromString(teamId),
                    ...(emailFallback !== undefined
                      ? { email: emailFallback }
                      : {}),
                    ...(mongoCustomerId !== undefined
                      ? { mongoCustomerId }
                      : {}),
                    ...(limit !== undefined ? { limit } : {}),
                  },
                }),
              ),
            );
            // The RPC success schema intentionally types orders as
            // `Schema.Unknown` (passthroughList) to avoid re-mirroring the rich
            // Mongoose Order shape in effect-schema; cast to the model type the
            // frontend already uses.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            return result.orders as OrderWithPickStatus[];
          },
        );
      },
      getReturnsByEmail: async (
        teamId,
        email,
        options,
      ): Promise<MerchantAppReturn[]> => {
        return profileSupportTicketRequest(
          "getSupportRelatedReturnsByEmail",
          { hasEmail: email.length > 0, teamId },
          async () => {
            const result = await effectApi.runPromise(
              catchApiErrors(
                client.supportTicketRelatedRecords.getSupportRelatedReturnsByEmail(
                  {
                    urlParams: {
                      teamId: MongoTeamId.fromString(teamId),
                      email,
                      ...(options?.limit !== undefined
                        ? { limit: options.limit }
                        : {}),
                    },
                  },
                ),
              ),
            );
            // Returns are a `Schema.Unknown` passthrough (see getOrders
            // above); cast to the model type the frontend already uses.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            return result.returns as MerchantAppReturn[];
          },
        );
      },
      getCustomerEventsByCustomerId: async (teamId, customerId, options) => {
        return profileSupportTicketRequest(
          "getSupportRelatedCustomerEventsByCustomerId",
          { customerId, teamId },
          async () => {
            const result = await effectApi.runPromise(
              catchApiErrorsSilent(
                client.supportTicketRelatedRecords.getSupportRelatedCustomerEventsByCustomerId(
                  {
                    urlParams: {
                      teamId: MongoTeamId.fromString(teamId),
                      customerId,
                      ...(options?.limit !== undefined
                        ? { limit: options.limit }
                        : {}),
                    },
                  },
                ),
              ),
            );
            if (!result) {
              return [];
            }
            // Customer events cross the Effect API as a `Schema.Unknown`
            // passthrough, so date fields like `timestamp` arrive as ISO
            // strings. Parse through the Zod schema (as the customer-app client
            // does) to coerce them into real `Date`s before they reach the
            // collection — otherwise downstream `timestamp.getTime()` throws.
            const customerEvents: CustomerEvent[] = [];
            for (const event of result.customerEvents) {
              const parsed = CustomerEventSchema.safeParse(event);
              if (parsed.success) {
                customerEvents.push(parsed.data);
              }
            }
            return customerEvents;
          },
        );
      },
    },
    drafts: {
      getDrafts: (supportTicketId: SupportTicketId) =>
        profileSupportTicketRequest(
          "getSupportTicketDrafts",
          { supportTicketId },
          () =>
            effectApi.runPromise(
              catchApiErrorsSilent(
                client.supportTicket.getSupportTicketDrafts({
                  path: { id: supportTicketId },
                }),
              ),
            ),
        ),
      saveDraft: (supportTicketId, payload) =>
        profileSupportTicketRequest(
          "saveSupportTicketDraft",
          { supportTicketId, threadId: payload.threadId },
          () =>
            effectApi
              .runPromise(
                catchApiErrors(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  client.supportTicket.saveSupportTicketDraft({
                    path: { id: supportTicketId },
                    payload,
                  } as Parameters<
                    typeof client.supportTicket.saveSupportTicketDraft
                  >[0]),
                ),
              )
              .then(invalidateTicketQueries(supportTicketId))
              .then(() => undefined),
        ),
      deleteDraft: (supportTicketId, draftId: DraftMessageId) =>
        profileSupportTicketRequest(
          "deleteSupportTicketDraft",
          { supportTicketId, draftId },
          () =>
            effectApi
              .runPromise(
                catchApiErrors(
                  client.supportTicket.deleteSupportTicketDraft({
                    path: { id: supportTicketId, draftId },
                  }),
                ),
              )
              .then(invalidateTicketQueries(supportTicketId)),
        ),
    },
    getSupportTicketSummary: (supportTicketId: SupportTicketId) =>
      queryClient
        ? queryClient.fetchQuery(
            supportTicketSummaryQueryOptions(effectApi, supportTicketId),
          )
        : fetchSupportTicketSummary(effectApi, supportTicketId),
  };
}
